import React from 'react';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';
import { X, ArrowLeft } from 'lucide-react';
import { TransactionsList } from '@/components/TransactionsList';

// ─── Modal Component ──────────────────────────────────────────────────────────

interface AdminTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AdminTransactionsModal: React.FC<AdminTransactionsModalProps> = ({ isOpen, onClose }) => {
  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.95)',
          backdropFilter: 'blur(4px)',
          zIndex: 1000,
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1001,
          width: 'min(640px, calc(100vw - 32px))',
          maxHeight: 'min(540px, calc(100vh - 64px))',
          background: 'hsl(246, 38%, 8%)',
          border: '1px solid hsl(246, 24%, 14%)',
          borderRadius: '0.75rem',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid hsl(246, 24%, 14%)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={onClose}
              title="Go back"
              style={{
                background: 'none',
                border: '1px solid hsl(246, 24%, 14%)',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                color: 'hsl(262, 83%, 66%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 4,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'hsl(262, 83%, 66%)'; e.currentTarget.style.background = 'rgba(139,92,246,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'hsl(246, 24%, 14%)'; e.currentTarget.style.background = 'none'; }}
            >
              <ArrowLeft size={16} />
            </button>
            <span style={{
              fontFamily: "'Syne', sans-serif",
              fontSize: 13,
              fontWeight: 700,
              color: 'hsl(262, 83%, 66%)',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}>
              TRANSACTION HISTORY
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid hsl(246, 24%, 14%)',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              color: 'hsl(246, 16%, 55%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 4,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'hsl(262, 83%, 66%)'; e.currentTarget.style.borderColor = 'hsl(262, 83%, 66%)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'hsl(246, 16%, 55%)'; e.currentTarget.style.borderColor = 'hsl(246, 24%, 14%)'; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable list */}
        <div style={{
          overflowY: 'auto',
          flex: 1,
        }}>
          <TransactionsList
            showExternalLinks={true}
            enabled={isOpen}
          />
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400..800&family=DM+Mono:wght@400;500&display=swap');
      `}</style>
    </>
  );
};

export default AdminTransactionsModal;
